/**
 * Group list and invitation status constants.
 * Use UPPER_CASE keys for registry/constant objects (Liran's Rules).
 */

import { GROUP_NAME_MAX_LENGTH, GROUP_NAME_MIN_LENGTH } from './groupName'

export const INVITATION_STATUS = {
  ACCEPTED: 'accepted',
  PENDING: 'pending',
} as const

export type InvitationStatus =
  (typeof INVITATION_STATUS)[keyof typeof INVITATION_STATUS]

/** Invitation direction: used for both friends and groups (no magic strings). */
export const INVITATION_TYPE = {
  RECEIVED: 'received',
  SENT: 'sent',
} as const

export type InvitationType =
  (typeof INVITATION_TYPE)[keyof typeof INVITATION_TYPE]

export const GROUP_LIST_ITEM_TYPE = {
  GROUP_ACTIVE: 'GROUP_ACTIVE',
  GROUP_PENDING: 'GROUP_PENDING',
} as const

export type GroupListItemType =
  (typeof GROUP_LIST_ITEM_TYPE)[keyof typeof GROUP_LIST_ITEM_TYPE]

/** Fallback display name when inviter profile is missing (Liran's rule: no magic strings in components). */
export const INVITER_FALLBACK_NAME = 'Someone'

/** Path prefix for group leaderboard screen (stack under Friends tab); append groupId. */
export const GROUP_LEADERBOARD_PATH = '/app/friends/group'

/** Label shown on group card badge when the current user created the group. */
export const GROUP_CARD_CREATOR_LABEL = 'Creator'

/** Subtitle under group name on leaderboard hero. */
export const GROUP_LEADERBOARD_SUBTITLE = 'Leaderboard'

/** Fallback when group has no name. */
export const GROUP_FALLBACK_NAME = 'Group'

/** Copy for group name edit (creator). */
export const GROUP_NAME_EDIT = {
  EDIT_LABEL: 'Edit group name',
  PLACEHOLDER: 'Group name',
  SAVE: 'Save',
  CANCEL: 'Cancel',
  TOO_SHORT: `Group name must be at least ${GROUP_NAME_MIN_LENGTH} characters`,
  TOO_LONG: `Group name must be at most ${GROUP_NAME_MAX_LENGTH} characters`,
  UPDATE_FAILED: 'Could not update group name. Try again.',
  SAVE_SUCCESS: 'Group name updated',
} as const

/** Empty state when group has no challenges yet. */
export const GROUP_LEADERBOARD_EMPTY_MESSAGE = 'No challenges in this group yet'

/** Label for current user on leaderboard (badge and display name fallback). */
export const GROUP_LEADERBOARD_YOU_LABEL = 'You'

/** Section title for joinable (PENDING/ACTIVE) group challenges on group page. */
export const GROUP_JOINABLE_CHALLENGES_TITLE = 'Challenges you can join'
/** Accept button label in joinable challenges section. */
export const GROUP_JOINABLE_ACCEPT_BUTTON_LABEL = 'Accept'
/** Fallback challenge name in joinable section when challenge.name is missing. */
export const GROUP_JOINABLE_CHALLENGE_FALLBACK_NAME = 'Challenge'

/** Challenge history section below group leaderboard. */
export const GROUP_CHALLENGE_HISTORY_TITLE = 'Challenge history'
/** Number of challenge history cards to show initially. */
export const GROUP_CHALLENGE_HISTORY_INITIAL_COUNT = 40
/** Page size for completed group challenges (each "load more" via scroll). */
export const GROUP_CHALLENGE_HISTORY_PAGE_SIZE = 40
/** Empty state when group has no completed challenges. */
export const GROUP_CHALLENGE_HISTORY_EMPTY_MESSAGE = 'No challenge history yet'
/** Button to load more challenge history cards. */
export const GROUP_CHALLENGE_HISTORY_SHOW_MORE = 'Show more'

/** Banner when viewing a completed group challenge you did not play in. */
export const GROUP_CHALLENGE_HISTORY_SPECTATOR_BANNER =
  'You did not take part in this challenge. Scores below are for the selected member.'

/** Center outcome label when the viewer did not participate (no personal win/loss). */
export const GROUP_CHALLENGE_HISTORY_SPECTATOR_OUTCOME_LABEL = 'VIEWING'

/** Placeholder for the viewer’s score column when they did not play (typographic dash). */
export const GROUP_CHALLENGE_HISTORY_SPECTATOR_SCORE_PLACEHOLDER = '—'

/** Delay (ms) before blur cancels group name edit (allows save button tap to register first). */
export const GROUP_NAME_EDIT_BLUR_CANCEL_DELAY_MS = 150

/** Success snackbar message after inviting members to a group. */
export const GROUP_INVITATION_SENT_MESSAGE = 'Invitations sent'

